/*
 * <oilogic-social-feed> — dependency-free horizontal slider for the social feed.
 * The track is a native CSS scroll-snap scroller, usable before/without JS.
 *
 * On connect it wires the prev/next arrows to scrollBy() and turns the track into
 * an INFINITE loop: clones the slide set on both sides ([clones][originals][clones]),
 * starts in the middle set, and once scrolling settles outside it silently shifts
 * by ±one set. The three regions are pixel-identical, so the jump is invisible.
 * Clones are inert + aria-hidden. Re-inits on Theme Editor reload.
 *
 * Markup hooks: [data-feed-track], [data-feed-prev], [data-feed-next].
 */
class OilogicSocialFeed extends HTMLElement {
  #ctrl = new AbortController();
  #looping = false;
  #setWidth = 0;
  #originals = [];
  #wrapTimer = 0;
  #resizeTimer = 0;
  #autoplayTimer = 0;
  #lastWidth = window.innerWidth;
  #dragging = false;
  #dragMoved = false;
  #dragStartX = 0;
  #dragStartScroll = 0;
  #velocity = 0;
  #lastX = 0;
  #lastT = 0;

  connectedCallback() {
    this.track = this.querySelector('[data-feed-track]');
    this.prevBtn = this.querySelector('[data-feed-prev]');
    this.nextBtn = this.querySelector('[data-feed-next]');
    if (!this.track) return;

    const { signal } = this.#ctrl;

    this.prevBtn?.addEventListener('click', () => this.#scroll(-1), { signal });
    this.nextBtn?.addEventListener('click', () => this.#scroll(1), { signal });
    this.track.addEventListener('scroll', () => this.#onScroll(), { passive: true, signal });
    window.addEventListener('resize', () => this.#onResize(), { passive: true, signal });

    // Mouse drag-to-scroll (touch/trackpad already scroll natively). A click
    // that follows a real drag is swallowed so card links don't fire.
    this.track.addEventListener('pointerdown', (e) => this.#dragStart(e), { signal });
    this.track.addEventListener('pointermove', (e) => this.#dragMove(e), { signal });
    // On window: capture starts only after the drag threshold, so a release
    // outside the track must still end the drag.
    window.addEventListener('pointerup', (e) => this.#dragEnd(e), { signal });
    window.addEventListener('pointercancel', (e) => this.#dragEnd(e), { signal });
    this.track.addEventListener('dragstart', (e) => e.preventDefault(), { signal });
    this.track.addEventListener(
      'click',
      (e) => {
        if (this.#dragMoved) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      { capture: true, signal }
    );

    // Autoplay pauses on hover/focus and when the tab is hidden.
    this.addEventListener('pointerenter', () => this.#pauseAutoplay(), { signal });
    this.addEventListener('pointerleave', () => this.#startAutoplay(), { signal });
    this.addEventListener('focusin', () => this.#pauseAutoplay(), { signal });
    this.addEventListener('focusout', () => this.#startAutoplay(), { signal });
    document.addEventListener('visibilitychange', () => this.#onVisibility(), { signal });

    this.dataset.ready = '';
    // Card widths are token-driven (not image-dependent), so layout is stable
    // by the next frame — safe to measure and build the loop then.
    requestAnimationFrame(() => this.#init());
  }

  disconnectedCallback() {
    clearTimeout(this.#wrapTimer);
    clearTimeout(this.#resizeTimer);
    this.#pauseAutoplay();
    this.#teardownLoop();
    this.#ctrl.abort();
    this.#ctrl = new AbortController();
  }

  #init() {
    this.#teardownLoop();
    const overflowing = this.track.scrollWidth - this.track.clientWidth > 1;
    this.toggleAttribute('data-no-overflow', !overflowing);
    if (overflowing) this.#setupLoop();
    this.#startAutoplay();
  }

  // ── Autoplay ──
  get #autoplayEnabled() {
    if (!this.hasAttribute('data-autoplay') || !this.#looping) return false;
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #startAutoplay() {
    this.#pauseAutoplay();
    if (!this.#autoplayEnabled) return;
    const secs = parseFloat(this.getAttribute('data-autoplay-speed')) || 4;
    this.#autoplayTimer = setInterval(() => this.#scroll(1), secs * 1000);
  }

  #pauseAutoplay() {
    clearInterval(this.#autoplayTimer);
    this.#autoplayTimer = 0;
  }

  #onVisibility() {
    if (document.hidden) this.#pauseAutoplay();
    else this.#startAutoplay();
  }

  #onResize() {
    // Mobile address-bar show/hide on scroll fires resize with a changed height
    // but same width; re-initing there resets the loop to slide 1 (the reported
    // mobile glitch). Only re-init on a real width change (orientation/breakpoint).
    if (window.innerWidth === this.#lastWidth) return;
    this.#lastWidth = window.innerWidth;
    clearTimeout(this.#resizeTimer);
    this.#resizeTimer = setTimeout(() => this.#init(), 150);
  }

  // ── Mouse drag ──
  // data-dragging lets the consumer's CSS switch the cursor and disable
  // scroll-snap for the duration, so the track follows the pointer 1:1;
  // re-enabling snap on release lands the track on the nearest card.
  #dragStart(e) {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    this.#dragging = true;
    this.#dragMoved = false;
    this.#dragStartX = e.clientX;
    this.#dragStartScroll = this.track.scrollLeft;
    this.#velocity = 0;
    this.#lastX = e.clientX;
    this.#lastT = e.timeStamp;
    // Grabbing mid-settle: setting scrollLeft below cancels the smooth glide,
    // and data-dragging takes over the snap-off state.
    delete this.track.dataset.settling;
    this.track.dataset.dragging = '';
  }

  #dragMove(e) {
    if (!this.#dragging) return;
    const dx = e.clientX - this.#dragStartX;
    if (!this.#dragMoved && Math.abs(dx) > 4) {
      this.#dragMoved = true;
      // Capture only once a real drag starts: capturing on pointerdown would
      // retarget the resulting click to the track, so card links never fire.
      this.track.setPointerCapture(e.pointerId);
    }
    if (this.#dragMoved) {
      this.track.scrollLeft = this.#dragStartScroll - dx;
      // Exponentially smoothed pointer velocity (px/ms) for the release fling.
      const dt = e.timeStamp - this.#lastT;
      if (dt > 0) {
        const v = (e.clientX - this.#lastX) / dt;
        this.#velocity = v * 0.6 + this.#velocity * 0.4;
        this.#lastX = e.clientX;
        this.#lastT = e.timeStamp;
      }
    }
  }

  #dragEnd(e) {
    if (!this.#dragging) return;
    this.#dragging = false;
    if (this.track.hasPointerCapture?.(e.pointerId)) this.track.releasePointerCapture(e.pointerId);
    // Hand off to a smooth glide BEFORE re-enabling snap: data-settling keeps
    // snap off, so removing data-dragging can't cause the browser's abrupt
    // no-animation jump to the nearest snap point.
    if (this.#dragMoved) {
      this.track.dataset.settling = '';
      this.#settle();
    }
    delete this.track.dataset.dragging;
    this.#onScroll(); // schedule snap re-enable + loop wrap once motion settles
    // Cleared on the next task so the capture-phase click handler above still
    // sees the flag for the click that ends this drag.
    setTimeout(() => {
      this.#dragMoved = false;
    });
  }

  // Glide from the release point to the nearest snap target, carrying the
  // fling's momentum: project ~160ms of travel at the release velocity, then
  // smooth-scroll to whichever card center is closest to that point.
  #settle() {
    const track = this.track;
    // Pointer moving left scrolls the content right, hence the sign flip.
    let momentum = -this.#velocity * 160;
    const cap = track.clientWidth;
    momentum = Math.max(-cap, Math.min(cap, momentum));
    const target = this.#nearestSnap(track.scrollLeft + momentum);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left: target, behavior: reduce ? 'auto' : 'smooth' });
  }

  // Scroll offset that centers the slide whose center is nearest to the
  // hypothetical scroll position `x` (slides snap-align to center, and the
  // symmetric scroll-padding cancels out of the math).
  #nearestSnap(x) {
    const track = this.track;
    const trackLeft = track.getBoundingClientRect().left;
    const viewCenter = x + track.clientWidth / 2;
    let best = x;
    let bestDist = Infinity;
    for (const slide of track.children) {
      const rect = slide.getBoundingClientRect();
      const slideCenter = rect.left - trackLeft + track.scrollLeft + rect.width / 2;
      const dist = Math.abs(slideCenter - viewCenter);
      if (dist < bestDist) {
        bestDist = dist;
        best = slideCenter - track.clientWidth / 2;
      }
    }
    return Math.max(0, Math.min(best, track.scrollWidth - track.clientWidth));
  }

  // ── Infinite loop ──
  #setupLoop() {
    this.#originals = Array.from(this.track.children);
    if (this.#originals.length === 0) return;

    const lead = document.createDocumentFragment();
    const trail = document.createDocumentFragment();
    for (const el of this.#originals) trail.append(this.#clone(el));
    for (const el of this.#originals) lead.append(this.#clone(el));
    this.track.append(trail);
    this.track.prepend(lead);

    // One set's pitch = distance between a middle-set item and its lead-set twin,
    // which already accounts for the gap that joins the two sets.
    const n = this.#originals.length;
    this.#setWidth = this.track.children[n].offsetLeft - this.track.children[0].offsetLeft;
    this.#looping = true;

    this.#jumpTo(this.#setWidth); // land on the first original (middle set)
  }

  #teardownLoop() {
    if (this.track) {
      for (const clone of this.track.querySelectorAll('[data-feed-clone]')) clone.remove();
    }
    this.#looping = false;
    this.#setWidth = 0;
  }

  #clone(el) {
    const clone = el.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.dataset.feedClone = '';
    if (clone.id) clone.removeAttribute('id');
    for (const node of clone.querySelectorAll('[id]')) node.removeAttribute('id');
    return clone;
  }

  // Instant (non-animated) scroll — used to reposition seamlessly.
  #jumpTo(left) {
    const prev = this.track.style.scrollBehavior;
    this.track.style.scrollBehavior = 'auto';
    this.track.scrollLeft = left;
    this.track.style.scrollBehavior = prev;
  }

  #onScroll() {
    // Never act mid-drag: a ±setWidth jump would break the pointer math.
    if (this.#dragging) return;
    // Act only once motion settles, so a smooth glide/arrow scroll is never cut.
    clearTimeout(this.#wrapTimer);
    this.#wrapTimer = setTimeout(() => this.#onSettled(), 120);
  }

  #onSettled() {
    if (this.#dragging) return;
    // The glide parked the track exactly on a snap position, so re-enabling
    // scroll-snap here cannot cause a visible jump.
    delete this.track.dataset.settling;
    if (this.#looping) this.#wrap();
  }

  // Keep the visible content in the middle set. Regions are identical, so a
  // ±setWidth shift is imperceptible.
  #wrap() {
    if (!this.#looping || this.#dragging) return;
    const w = this.#setWidth;
    const x = this.track.scrollLeft;
    if (x < w) this.#jumpTo(x + w);
    else if (x >= w * 2) this.#jumpTo(x - w);
  }

  // Scroll by roughly one card (first item width + gap), in the given direction.
  #scroll(direction) {
    const item = this.track.firstElementChild;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const step = item ? item.getBoundingClientRect().width + gap : this.track.clientWidth * 0.8;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({ left: step * direction, behavior: reduce ? 'auto' : 'smooth' });
  }
}

customElements.define('oilogic-social-feed', OilogicSocialFeed);
